/**
 * 管理编辑器表格图层的 Canvas 预览资源和单元格编辑会话
 * 依赖字体准备与表格渲染器，预览复用通用资源倍率并以最大画布尺寸限制像素，导出使用独立倍率
 */
import { EdgelessObject, normalizeResultScale, type IFramework, type IResultOptions } from '@/framework';
import { LayerType, MAX_STAGE_HEIGHT, MAX_STAGE_WIDTH, type ITableLayer } from '@/version-types';
import { prepareTableLayerDocumentFonts } from './font-measurement';
import { renderLayerContentToCanvas, renderLayerContentToCanvasSync } from './render/render-to-canvas';

export class EdgelessTableLayer extends EdgelessObject {
    static type = LayerType.TABLE;

    /** 编辑会话和资源代次共同阻止过期异步渲染结果覆盖当前画面。 */
    private cellEditSessionId: number | null = null;
    private suppressedCellId: string | null = null;
    private resourceGeneration = 0;
    /** 最近一次包含全部单元格内容的资源，用于同步结束编辑时立即恢复画面。 */
    private lastCompleteResource: HTMLCanvasElement | null = null;
    /** 提交前同步生成的最终资源，避免退出编辑时短暂恢复旧文案。 */
    private preparedCellEditCommitResource: { sessionId: number; canvas: HTMLCanvasElement } | null = null;

    constructor(layerId: string, framework: IFramework) {
        super(layerId, framework.service);
    }

    private getLayer() {
        return this.service.flatLayer.getLayer(this.layerId) as ITableLayer;
    }

    private async renderResource(layer: ITableLayer, suppressedCellId?: string | null) {
        await prepareTableLayerDocumentFonts(this.service, layer);
        const canvas = await renderLayerContentToCanvas(layer, {
            scale: super.getResourceScale({
                naturalWidth: MAX_STAGE_WIDTH,
                naturalHeight: MAX_STAGE_HEIGHT,
                layerSize: layer,
            }),
            suppressedCellId,
        });
        await super.drawAiWatermarkAsync(canvas);
        return canvas;
    }

    private applyInteractiveResource(canvas: HTMLCanvasElement) {
        this.resource = canvas;
        this.service.edgelessCanvas.setDirty({ object: this });
    }

    /** 开始编辑时生成隐藏当前单元格内容的画布资源。 */
    async beginCellEditResource(args: { cellId: string; sessionId: number }) {
        const generation = ++this.resourceGeneration;
        this.cellEditSessionId = args.sessionId;
        this.suppressedCellId = args.cellId;
        this.preparedCellEditCommitResource = null;

        const canvas = await this.renderResource(this.getLayer(), args.cellId);
        if (
            generation !== this.resourceGeneration ||
            this.cellEditSessionId !== args.sessionId ||
            this.suppressedCellId !== args.cellId
        ) {
            return false;
        }

        this.applyInteractiveResource(canvas);
        return true;
    }

    /** 取消编辑时等待完整资源恢复，成功后结束当前会话。 */
    async cancelCellEditResource(args: { sessionId: number }) {
        if (this.cellEditSessionId !== args.sessionId) return false;

        const generation = ++this.resourceGeneration;
        const canvas = await this.renderResource(this.getLayer());
        if (generation !== this.resourceGeneration || this.cellEditSessionId !== args.sessionId) return false;

        this.cellEditSessionId = null;
        this.suppressedCellId = null;
        this.preparedCellEditCommitResource = null;
        this.lastCompleteResource = canvas;
        this.applyInteractiveResource(canvas);
        return true;
    }

    /** 用最终图层同步准备提交资源；准备阶段不改变当前画面。 */
    prepareCellEditCommitResourceSync(args: { sessionId: number; layer: ITableLayer }) {
        if (this.cellEditSessionId !== args.sessionId) return false;

        const canvas = renderLayerContentToCanvasSync(args.layer, {
            scale: super.getResourceScale({
                naturalWidth: MAX_STAGE_WIDTH,
                naturalHeight: MAX_STAGE_HEIGHT,
                layerSize: args.layer,
            }),
        });
        this.preparedCellEditCommitResource = {
            sessionId: args.sessionId,
            canvas,
        };
        return true;
    }

    private endCellEditResourceSync(sessionId: number, applyPreparedCommitResource: boolean) {
        if (this.cellEditSessionId !== sessionId) return false;

        ++this.resourceGeneration;
        this.cellEditSessionId = null;
        this.suppressedCellId = null;
        const preparedResource =
            applyPreparedCommitResource && this.preparedCellEditCommitResource?.sessionId === sessionId
                ? this.preparedCellEditCommitResource.canvas
                : null;
        this.preparedCellEditCommitResource = null;
        const completeResource = preparedResource ?? this.lastCompleteResource;
        if (completeResource) {
            this.lastCompleteResource = completeResource;
            this.applyInteractiveResource(completeResource);
        }
        if (this.service.flatLayer.getLayer(this.layerId)) {
            void this.update(false);
        }
        return true;
    }

    /** 提交编辑时应用已准备的最终资源，再异步补齐完整资源处理。 */
    finishCellEditResourceSync(args: { sessionId: number }) {
        return this.endCellEditResourceSync(args.sessionId, true);
    }

    /** 编辑器卸载时使当前会话失效，避免未完成渲染回写。 */
    abortCellEditResourceSync(args: { sessionId: number }) {
        return this.endCellEditResourceSync(args.sessionId, false);
    }

    override supportsCanvasResult() {
        return true;
    }

    override async getResult({ aiWatermark = false, scale = 1 }: IResultOptions = {}) {
        const layer = this.getLayer();
        const renderBox = this.getBaseInfo();
        await prepareTableLayerDocumentFonts(this.service, layer);
        const canvas = await renderLayerContentToCanvas(layer, { scale: normalizeResultScale(scale) });
        if (aiWatermark) {
            await super.drawAiWatermarkAsync(canvas);
        }
        return { canvas, renderBox };
    }

    override async loadResource() {
        const generation = ++this.resourceGeneration;
        const suppressedCellId = this.suppressedCellId;
        const canvas = await this.renderResource(this.getLayer(), suppressedCellId);
        if (generation !== this.resourceGeneration) return;

        if (!suppressedCellId) {
            this.lastCompleteResource = canvas;
        }
        return canvas;
    }
}
